import type { TruckFastestLap, TruckLapBoards, TrucksPrivate, TrucksState, TruckView } from "@townsy/contracts";
import type { TruckMotion } from "@townsy/world";

/**
 * The arena's shared state as the browser received it (`venue.state`). The server is the authority; this only checks
 * the shape so a malformed message can never break the overlay or the 3D.
 */
export function asTrucksState(shared: unknown): TrucksState | null {
  if (!shared || typeof shared !== "object") return null;
  const s = shared as Partial<TrucksState>;
  if (!Array.isArray(s.trucks) || !Array.isArray(s.drivers) || !s.race || typeof s.race !== "object" || typeof s.now !== "number") return null;
  return s as TrucksState;
}

const finite = (v: unknown, fallback = 0): number => (typeof v === "number" && Number.isFinite(v) ? v : fallback);

/** The fastest laps boards from this visitor's `venue.private` (ADR-0032), or null when absent or malformed. */
export function asLapBoards(mine: unknown): TruckLapBoards | null {
  const b = (mine as Partial<TrucksPrivate> | null)?.boards;
  if (!b || typeof b !== "object" || typeof b.day !== "string" || !Array.isArray(b.today) || !Array.isArray(b.allTime)) return null;
  const lines = (list: unknown[]): TruckFastestLap[] =>
    list
      .filter((l): l is TruckFastestLap => !!l && typeof l === "object" && typeof (l as TruckFastestLap).actorId === "string" && typeof (l as TruckFastestLap).label === "string" && Number.isFinite((l as TruckFastestLap).lapMs))
      .slice(0, 50);
  return { day: b.day, today: lines(b.today), allTime: lines(b.allTime) };
}

/** A truck view as a motion state (non-finite numbers become 0). */
export function motionOf(v: TruckView): TruckMotion {
  return { x: finite(v.x), z: finite(v.z), heading: finite(v.heading), speed: finite(v.speed), y: Math.max(0, finite(v.y)), vy: finite(v.vy), airborne: v.airborne === true };
}

export function truckView(state: TrucksState | null, id: string): TruckView | null {
  return state?.trucks.find((t) => t.id === id) ?? null;
}

/** "1:02.3" (minutes, seconds, tenths). */
export function formatLapTime(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return "–";
  const tenths = Math.max(0, Math.floor(ms / 100));
  const m = Math.floor(tenths / 600);
  const s = Math.floor((tenths % 600) / 10);
  return `${m}:${String(s).padStart(2, "0")}.${tenths % 10}`;
}
